import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import 'react-toastify/dist/ReactToastify.min.css';
import _ from 'lodash';
import { JsonLd } from 'react-schemaorg';

import { getConfig } from '../../../AppConfig';
import { getImage, getText, isRestaurantApp } from '../../../util/Helpers';
import useSeo from '../../../hooks/useSeo';

const config = getConfig();
const isRestaurant = isRestaurantApp();

function SeoProvider() {
  const location = useLocation();

  const { restaurantId, restaurantDetails } = useSelector(({ restaurant }) => ({
    restaurantDetails: restaurant?.selectedRestaurantData,
    restaurantId: restaurant?.selectedRestaurantId,
  }));

  const {
    pageTitle: currentPageTitle,
    pageDescription: currentPageDescription,
    pageKeywords: currentPageKeywords,
    jsonItem,
    canonicalURL,
    reset: resetSeoData,
  } = useSeo();

  useEffect(() => {
    resetSeoData();
  }, [location]);

  // Self-canonicalize to the current host; avoid cross-domain canonicals
  // which cause "Alternate page with proper canonical tag" across subdomains.

  const websiteName = isRestaurant
    ? getText(config, 'site_title', config.textsPlaceholder.consumer_tab_title)
    : getText(
        config,
        'consumer_tab_title',
        config.textsPlaceholder.consumer_tab_title
      );
  const websiteTitle = isRestaurant
    ? getText(config, 'site_title', config.textsPlaceholder.consumer_tab_title)
    : getText(
        config,
        'consumer_tab_title',
        config.textsPlaceholder.consumer_tab_title
      );
  const websiteDescription =
    currentPageDescription ?? isRestaurant
      ? getText(
          config,
          'site_description',
          config.locales.consumer_meta_description
        )
      : getText(
          config,
          'consumer_meta_description',
          config.textsPlaceholder.consumer_meta_description
        );
  const websiteKeywords = isRestaurant
    ? getText(
        config,
        'site_keywords',
        config.textsPlaceholder.consumer_meta_keywords
      )
    : getText(
        config,
        'consumer_meta_keywords',
        config.textsPlaceholder.consumer_meta_keywords
      );
  const websiteOgType = isRestaurant
    ? 'restaurant.restaurant'
    : 'business.business';
  const websiteBanner = isRestaurant
    ? getImage(config, 'first_slide', null)
    : getImage(config, 'consumer_header_main', null);

  const websiteIcon = isRestaurant
    ? getImage(config, 'favicon', null)
    : getImage(config, 'consumer_favicon', null);

  const altName = restaurantDetails?.name?.split(' - ')[0] || '';

  const getCanonicalUrl = () => {
    if (canonicalURL) {
      return canonicalURL.startsWith('http')
        ? canonicalURL
        : `https://${canonicalURL}`;
    }
    const { hostname, pathname, origin } = window.location;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    // Use real origin in dev; force https in prod to avoid "Page with redirect"
    return isLocalhost
      ? `${origin}${pathname}`
      : `https://${hostname}${pathname}`;
  };

  const url = `${getCanonicalUrl()}`;
  const domain = new URL(url).hostname;
  const { pathname: path, host, origin, hostname } = window.location;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const baseOrigin = isLocalhost ? origin : `https://${host}`;
  const bannerAbsolute = (() => {
    if (!_.isString(websiteBanner)) {
      return null;
    }
    if (websiteBanner.startsWith('//')) {
      return `https:${websiteBanner}`;
    }
    if (websiteBanner.startsWith('http')) {
      return websiteBanner;
    }
    return `${window.location.protocol}${websiteBanner}`;
  })();
  const isUtility =
    /^\/(resetpassword|checkout|cart|order-success|track-orders)(\/|$)/.test(
      path
    ) || path.startsWith('/account-details/');

  return (
    <>
      <Helmet>
        <title>
          {currentPageTitle
            ? `${currentPageTitle} - ${websiteTitle}`
            : websiteTitle}
        </title>
        <meta
          property="og:title"
          content={
            currentPageTitle
              ? `${currentPageTitle} - ${websiteTitle}`
              : websiteTitle
          }
        />
        <meta property="og:site_name" content={websiteTitle} />
        <meta property="og:url" content={getCanonicalUrl()} />
        <meta property="og:type" content={websiteOgType} />
        <meta
          name="description"
          content={currentPageDescription ?? websiteDescription}
        />
        <meta
          property="og:description"
          content={currentPageDescription ?? websiteDescription}
        />
        <meta property="og:locale" content="en_US" />
        <meta
          name="keywords"
          content={currentPageKeywords ?? websiteKeywords}
        />
        {!_.isNil(bannerAbsolute) && (
          <meta property="og:image" content={bannerAbsolute} />
        )}
        {!_.isNil(bannerAbsolute) && (
          <meta name="twitter:card" content="summary_large_image" />
        )}
        {_.isNil(bannerAbsolute) && (
          <meta name="twitter:card" content="summary" />
        )}
        {!_.isNil(bannerAbsolute) && (
          <meta name="twitter:image" content={bannerAbsolute} />
        )}
        {!_.isNil(websiteIcon) && !_.isNil(window?.location?.protocol) && (
          <link
            rel="apple-touch-icon"
            href={`${window.location.protocol}${websiteIcon}`}
          />
        )}
        {!_.isNil(websiteIcon) && !_.isNil(window?.location?.protocol) && (
          <link rel="icon" href={`${window.location.protocol}${websiteIcon}`} />
        )}
        <meta name="business_timezone" content={config.timezone} />
        <meta name="app_version" content={config.version} />
        {isUtility ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : (
          <meta
            name="robots"
            content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
          />
        )}
        <link rel="canonical" href={getCanonicalUrl()} />
      </Helmet>
      {isRestaurant && restaurantDetails && restaurantId && (
        <JsonLd
          item={
            jsonItem ?? {
              '@context': 'https://schema.org',
              '@type': 'Restaurant',
              name: websiteName,
              alternateName: [altName, domain],
              description: websiteDescription,
              keywords: websiteKeywords,
              telephone: restaurantDetails.phone,
              url: `${baseOrigin}/restaurant/${restaurantId}`,
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `${baseOrigin}/restaurant/${restaurantId}`,
              },
              address: {
                '@type': 'PostalAddress',
                streetAddress: restaurantDetails.address,
                addressLocality: restaurantDetails.city,
                addressRegion: restaurantDetails.state,
                postalCode: restaurantDetails.zipcode,
                addressCountry: 'US',
              },
              geo: {
                '@type': 'GeoCoordinates',
                latitude: restaurantDetails.latitude,
                longitude: restaurantDetails.longitude,
              },
              menu: `${baseOrigin}/menu`,
              servesCuisine:
                _.startCase(restaurantDetails?.tags[0]?.name) || 'American',
              image: bannerAbsolute ? [bannerAbsolute] : undefined,
              logo: websiteIcon?.startsWith('//')
                ? `https:${websiteIcon}`
                : websiteIcon,
              publicAccess: true,
            }
          }
        />
      )}
      {!isRestaurant && (
        <JsonLd
          item={
            jsonItem ?? {
              '@context': 'https://schema.org',
              '@type': 'OnlineBusiness',
              name: websiteName,
              description: websiteDescription,
              keywords: websiteKeywords,
              image: bannerAbsolute ? [bannerAbsolute] : undefined,
              url: `${baseOrigin}`,
              logo: websiteIcon,
            }
          }
        />
      )}
      {/* Always provide WebSite schema to reinforce per-subdomain site name */}
      <JsonLd
        item={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: websiteTitle,
          url: baseOrigin,
        }}
      />
      {/* Organization schema for brand identity */}
      <JsonLd
        item={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: websiteName,
          url: baseOrigin,
        }}
      />
    </>
  );
}

export default SeoProvider;
